// ============================================================
// HOUSE OF CARDS — JumpscareEngine v1
// ------------------------------------------------------------
// This game has no player body. The camera sees only the room.
// That emptiness is the weapon: at any moment the room can be
// REPLACED — for a heartbeat — by something that should not be
// there, and you have ONE key to throw it off before it lands.
//
// This module owns the whole scare lifecycle:
//   • SCARE_BANK ........ pure data. add scares here forever.
//   • useJumpscareScheduler  arms a random timer while you search
//   • <Jumpscare> ....... the full-screen takeover + single-key QTE
//   • SVG fiends ........ self-contained, no external art needed
//
// CONTRACT (kept deliberately small so App.js stays clean):
//   const js = useJumpscareScheduler({ active, cursed, onFire });
//   ...later, when js.current is set, render:
//   <Jumpscare scare={js.current}
//              onSurvive={js.survive}
//              onCaught={js.caught} />
//
// Survive  → scare melts away, scheduler re-arms, play continues.
// Caught   → App.die() runs with the scare's death line.
//
// DESIGN NOTES FOR FUTURE ME (caP'n × V'ael):
//   - For now every scare is ONE key. No combos yet — the roadmap
//     says single key, so the `keys` array is length 1 and the QTE
//     resolves on the first correct hit. The data shape ALREADY
//     supports a sequence; flip MULTI_KEY on later and the runner
//     will walk the whole array. (left a clean seam, partner.)
//   - "monster" scares are the real thing lunging at the lens.
//   - "masquerade" scares are mundane — a toy snapping on, a frame
//     falling — dressed as a shadow that turns out to be nothing
//     (or... is it?). Same QTE, lower lethality, comic-relief
//     death lines. The Tenant is your hype man.
//   - Everything respects prefers-reduced-motion: the lunge still
//     happens (it must — it's the gameplay) but the violent shake
//     and strobe are dialed down to a hard cut.
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from "react";
import { RM, emberGlow } from "./theme";

const REDUCED =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;


// ============================================================
// SVG FIENDS — each takes {phase} ("lunge" | "win" | "fail")
// and draws itself huge, filling the takeover. No external art.
// Keep them in the RM palette via theme tokens only.
// ============================================================

// THE TENANT — the bereaved woman, too close, mouth wrong.
function FiendTenant({ phase }) {
  const eyeGlow = phase === "fail" ? RM.fleshGlow : RM.ember;
  return (
    <svg viewBox="0 0 400 400" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
      <defs>
        <radialGradient id="js-tenant-skin" cx="50%" cy="42%" r="60%">
          <stop offset="0%" stopColor={RM.bone} stopOpacity="0.92" />
          <stop offset="55%" stopColor={RM.flesh} stopOpacity="0.85" />
          <stop offset="100%" stopColor={RM.char0} stopOpacity="0.98" />
        </radialGradient>
      </defs>
      {/* head mass */}
      <ellipse cx="200" cy="205" rx="150" ry="185" fill="url(#js-tenant-skin)" />
      {/* hollow cheeks */}
      <path d="M120 250 Q145 300 175 320 Q140 300 120 250 Z" fill={RM.char0} opacity="0.5" />
      <path d="M280 250 Q255 300 225 320 Q260 300 280 250 Z" fill={RM.char0} opacity="0.5" />
      {/* eyes — wide, lid pulled */}
      <ellipse cx="150" cy="170" rx="34" ry="40" fill={RM.char0} />
      <ellipse cx="250" cy="170" rx="34" ry="40" fill={RM.char0} />
      <circle cx="150" cy="176" r="13" fill={eyeGlow}>
        {!REDUCED && phase === "lunge" && (
          <animate attributeName="r" values="11;16;11" dur="0.4s" repeatCount="indefinite" />
        )}
      </circle>
      <circle cx="250" cy="176" r="13" fill={eyeGlow}>
        {!REDUCED && phase === "lunge" && (
          <animate attributeName="r" values="11;16;11" dur="0.4s" repeatCount="indefinite" />
        )}
      </circle>
      {/* the mouth — opening too wide, a grief that became a hunger */}
      <path
        d={phase === "fail"
          ? "M120 280 Q200 400 280 280 Q200 360 120 280 Z"
          : "M140 290 Q200 340 260 290 Q200 320 140 290 Z"}
        fill={RM.char0}
      />
      <path
        d={phase === "fail"
          ? "M120 280 Q200 400 280 280"
          : "M140 290 Q200 340 260 290"}
        fill="none"
        stroke={RM.vein}
        strokeWidth="4"
      />
      {/* veins crawling up from the jaw */}
      <path d="M170 330 Q165 360 150 380 M230 330 Q235 360 250 380" stroke={RM.vein} strokeWidth="3" fill="none" opacity="0.7" />
    </svg>
  );
}

// THE CHILD — small, pale, under the blanket-shape, sitting up wrong.
function FiendChild({ phase }) {
  return (
    <svg viewBox="0 0 400 400" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
      <rect x="0" y="0" width="400" height="400" fill={RM.char0} />
      {/* blanket mound */}
      <path d="M60 400 Q200 230 340 400 Z" fill={RM.char3} />
      {/* small head rising */}
      <ellipse cx="200" cy="230" rx="64" ry="74" fill={RM.bone} opacity="0.9" />
      {/* dark sockets */}
      <ellipse cx="176" cy="220" rx="15" ry="20" fill={RM.char0} />
      <ellipse cx="224" cy="220" rx="15" ry="20" fill={RM.char0} />
      <circle cx="176" cy="224" r="5" fill={phase === "fail" ? RM.fleshGlow : RM.emberDim} />
      <circle cx="224" cy="224" r="5" fill={phase === "fail" ? RM.fleshGlow : RM.emberDim} />
      {/* a small, patient smile */}
      <path d="M172 262 Q200 282 228 262" stroke={RM.vein} strokeWidth="3" fill="none" />
      {/* one reaching hand */}
      <path d="M250 300 Q300 280 330 300 Q300 295 270 320 Z" fill={RM.bone} opacity="0.85" />
    </svg>
  );
}

// THE HALL THING — pure silhouette, all teeth, the dark made solid.
function FiendMaw({ phase }) {
  const open = phase === "fail" ? 1 : 0.7;
  return (
    <svg viewBox="0 0 400 400" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
      <rect x="0" y="0" width="400" height="400" fill={RM.char0} />
      <ellipse cx="200" cy="200" rx="220" ry="220" fill={RM.fleshDeep} opacity="0.25" />
      {/* upper teeth */}
      {[40, 95, 150, 205, 260, 315].map((x, i) => (
        <path key={`u${i}`} d={`M${x} 120 L${x + 27} 120 L${x + 13} ${120 + 60 * open} Z`} fill={RM.bone} />
      ))}
      {/* lower teeth */}
      {[68, 123, 178, 233, 288].map((x, i) => (
        <path key={`l${i}`} d={`M${x} 400 L${x + 27} 400 L${x + 13} ${400 - 60 * open} Z`} fill={RM.bone} opacity="0.9" />
      ))}
      {/* throat */}
      <ellipse cx="200" cy="220" rx="90" ry={70 * open} fill={RM.char0} />
      <ellipse cx="200" cy="220" rx="90" ry={70 * open} fill="none" stroke={RM.vein} strokeWidth="3" />
    </svg>
  );
}

// ── MASQUERADE FIENDS (mundane things wearing a shadow) ───────

// THE TOY — a jack/clown that just... turned itself on.
function FiendToy({ phase }) {
  return (
    <svg viewBox="0 0 400 400" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
      <rect x="0" y="0" width="400" height="400" fill={RM.char1} />
      {/* shadow it casts — reads as a monster for one frame */}
      <ellipse cx="200" cy="210" rx="170" ry="180" fill={RM.char0} opacity="0.55" />
      {/* the toy head */}
      <circle cx="200" cy="200" r="96" fill={RM.bone} opacity="0.92" />
      <circle cx="170" cy="185" r="11" fill={RM.char0} />
      <circle cx="230" cy="185" r="11" fill={RM.char0} />
      {/* painted cheeks */}
      <circle cx="158" cy="215" r="14" fill={RM.flesh} opacity="0.6" />
      <circle cx="242" cy="215" r="14" fill={RM.flesh} opacity="0.6" />
      {/* a wide painted grin — harmless. probably. */}
      <path d="M150 230 Q200 275 250 230" stroke={RM.vein} strokeWidth="5" fill="none" />
      {/* a little hat */}
      <path d="M150 120 L250 120 L200 60 Z" fill={RM.fleshDeep} />
      <circle cx="200" cy="58" r="10" fill={phase === "fail" ? RM.fleshGlow : RM.gold} />
    </svg>
  );
}

// THE FALLING FRAME — a picture toppling, its shadow a lunging shape.
function FiendFrame({ phase }) {
  const tilt = phase === "fail" ? 22 : 12;
  return (
    <svg viewBox="0 0 400 400" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
      <rect x="0" y="0" width="400" height="400" fill={RM.char1} />
      {/* the cast shadow — momentarily a person */}
      <path d="M120 400 Q200 120 280 400 Z" fill={RM.char0} opacity="0.6" />
      <g transform={`rotate(${tilt} 200 200)`}>
        <rect x="120" y="90" width="160" height="220" fill={RM.char3} stroke={RM.gold} strokeWidth="6" />
        <rect x="138" y="108" width="124" height="184" fill={RM.char0} />
        {/* a face in the portrait, watching */}
        <ellipse cx="200" cy="180" rx="40" ry="52" fill={RM.bone} opacity="0.5" />
        <circle cx="186" cy="172" r="6" fill={RM.char0} />
        <circle cx="214" cy="172" r="6" fill={RM.char0} />
        <path d="M186 205 Q200 215 214 205" stroke={RM.vein} strokeWidth="2" fill="none" />
      </g>
    </svg>
  );
}

export const FIENDS = {
  tenant: FiendTenant,
  child: FiendChild,
  maw: FiendMaw,
  toy: FiendToy,
  frame: FiendFrame,
};

// ============================================================
// SCARE_BANK — PURE DATA. This is the file you grow forever.
// ------------------------------------------------------------
// id        unique
// kind      "monster" | "masquerade"
// fiend     key into FIENDS
// key       the single key to press to throw it off (uppercase)
// windowMs  how long you have (monsters punish; masquerades forgive)
// title     the big word that slams in over the fiend
// prompt    line under the keycap
// survive   toast-ish line shown for a beat when you fend it off
// death     the death line passed to App.die() if you're too slow
// rooms     optional whitelist of room ids; omit = anywhere
// minProgress  optional: only eligible once player has N unlocked doors
// weight    relative odds of being picked (default 1)
// ============================================================
export const SCARE_BANK = [
  // ── MONSTERS ────────────────────────────────────────────
  {
    id: "tenant_lunge",
    kind: "monster",
    fiend: "tenant",
    key: "F",
    windowMs: 1400,
    title: "SHE'S RIGHT THERE",
    prompt: "Snap your eyes off her — find the lights and pop them",
    survive: "You wrench your eyes away. The wallpaper is just wallpaper. For now.",
    death: "You looked too long. She was always closer than the room let on.",
    weight: 3,
  },
  {
    id: "maw_hall",
    kind: "monster",
    fiend: "maw",
    key: "G",
    windowMs: 1300,
    title: "THE DARK HAS TEETH",
    prompt: "Pull your focus back — pop every light",
    survive: "The hall swallows its own grin and pretends to be a hall again.",
    death: "The house was hungry. You stood still long enough to be a meal.",
    rooms: ["hall", "landing"],
    weight: 2,
  },
  {
    id: "child_rise",
    kind: "monster",
    fiend: "child",
    key: "H",
    windowMs: 1500,
    title: "IT SAT UP",
    prompt: "Don't watch it smile — pop the lights instead",
    survive: "The little shape settles back under the blanket. You did not imagine it.",
    death: "It only wanted you to stay. Now you will. Fifty years young.",
    minProgress: 3,
    weight: 2,
  },

  // ── MASQUERADES (mundane, lower stakes, the hype man) ────
  {
    id: "toy_on",
    kind: "masquerade",
    fiend: "toy",
    key: "J",
    windowMs: 2000,
    title: "SOMETHING TURNED ON",
    prompt: "Bat the little lights away",
    survive: "...it's a toy. Just a toy, grinning in the dark. Its key wound down on its own. Sure.",
    death: "You froze at a wind-up toy. The Tenant is, somewhere, absolutely losing it.",
    weight: 1,
  },
  {
    id: "frame_fall",
    kind: "masquerade",
    fiend: "frame",
    key: "K",
    windowMs: 2100,
    title: "A SHAPE LUNGED",
    prompt: "Slap the lights down before it lands",
    survive: "A picture frame. It only fell. The shadow it threw was the scary part — and the shadow's gone.",
    death: "Killed by a falling picture. Technically. The portrait looks smug about it.",
    weight: 1,
  },
];

// Pick a scare eligible for the current room + progress, by weight.
export function pickScare(roomId, unlockedCount = 0) {
  const pool = SCARE_BANK.filter((s) => {
    if (s.rooms && !s.rooms.includes(roomId)) return false;
    if (s.minProgress && unlockedCount < s.minProgress) return false;
    return true;
  });
  if (!pool.length) return null;
  const total = pool.reduce((n, s) => n + (s.weight || 1), 0);
  let r = Math.random() * total;
  for (const s of pool) {
    r -= s.weight || 1;
    if (r <= 0) return s;
  }
  return pool[pool.length - 1];
}

// ============================================================
// useJumpscareScheduler — NO timer. Scares are EVENT-DRIVEN: App
// calls maybeScare() only at genuinely tense moments (a big beat
// like a puzzle/door/key-item, or opening a scary closeup). Even
// then a scare only lands rarely, and never within the cooldown of
// the last one. This is the whole anti-spam fix — there is no
// background loop that can cluster scares anymore.
// ============================================================
export function useJumpscareScheduler({
  roomId,                 // current room id (for room-gated scares)
  unlockedCount = 0,      // progress gate
  cursed = false,         // Cursed Mode = an INTENSITY MULTIPLIER on the baseline
  enabled = true,         // master off-switch
}) {
  const [current, setCurrent] = useState(null);

  // Room + progress are read at FIRE time.
  const roomRef = useRef(roomId);
  const progressRef = useRef(unlockedCount);
  useEffect(() => { roomRef.current = roomId; }, [roomId]);
  useEffect(() => { progressRef.current = unlockedCount; }, [unlockedCount]);

  // ── INTENSITY MODEL — "earned," not "on a clock" ──────────────
  // A scare is only ever CONSIDERED when App reports a tense moment.
  // Two hard gates make them rare and unbunched:
  //   1) COOLDOWN — a fixed, stable minimum gap since the last scare.
  //      (Stable: computed ONCE per scare, never re-rolled — that was
  //       the old clustering bug.)
  //   2) BASE_CHANCE — even off cooldown, most tense moments pass
  //      quietly. A moment's own `weight` (0..1) scales this, so a
  //      shadow-thing closeup is scarier than picking up a ball.
  // Cursed shortens the cooldown and lifts the odds via CURSE_RAMP.
  const COOLDOWN_MS = 70000;   // ≥70s between scares in regular play
  const BASE_CHANCE = 0.2;     // ~1 in 5 eligible moments, at weight 1
  const CURSE_RAMP = 0.55;     // Cursed: cooldown ×0.55, chance ÷0.55

  const lastScareRef = useRef(0);
  const enabledRef = useRef(enabled);
  const cursedRef = useRef(cursed);
  enabledRef.current = enabled;
  cursedRef.current = cursed;

  // The ONLY way an ambient scare can fire. Call at a tense moment.
  // weight: 0..1 relative scariness of the moment (default 0.6).
  const maybeScare = useCallback((weight = 0.6) => {
    if (!enabledRef.current) return;
    if (current) return; // one at a time
    const now = Date.now();
    const ramp = cursedRef.current ? CURSE_RAMP : 1;
    const cd = COOLDOWN_MS * ramp;
    if (now - lastScareRef.current < cd) return; // still in the quiet
    const chance = Math.min(0.85, (BASE_CHANCE / ramp) * weight);
    if (Math.random() > chance) return;          // most moments pass quietly
    const scare = pickScare(roomRef.current, progressRef.current);
    if (scare) {
      lastScareRef.current = now;
      setCurrent(scare);
    }
  }, [current]);

  const survive = useCallback(() => {
    lastScareRef.current = Date.now();
    setCurrent(null);
  }, []);

  const caught = useCallback(() => {
    const c = current;
    lastScareRef.current = Date.now();
    setCurrent(null);
    return c;
  }, [current]);

  // Scripted/forced scare (e.g. a cursed drawer). Bypasses the odds
  // but still respects the cooldown is reset so nothing chains after.
  const trigger = useCallback((id) => {
    const forced = id
      ? SCARE_BANK.find((s) => s.id === id)
      : pickScare(roomRef.current, progressRef.current);
    if (forced) {
      lastScareRef.current = Date.now();
      setCurrent(forced);
    }
  }, []);

  return { current, survive, caught, trigger, maybeScare };
}

// ============================================================
// DOT PUZZLE — the new "throw it off" mechanic.
// ------------------------------------------------------------
// When a scare lands you must TAP scattered dots before the
// window closes. The dots are the thing your eye snaps to when
// your guard is down — pop them and you wrench focus back.
//   • Easy (regular) mode : 3 dots, TRIPLED window (forgiving)
//   • Cursed mode         : 6 dots, the raw window (punishing)
//   • Easy mode           : tapping empty space does nothing
//   • Cursed mode         : a wrong tap means it gets you
// The dot count + difficulty are passed down from App via the
// `cursed` flag so this stays a one-prop tuning seam.
// ============================================================
const EASY_TIME_MULT = 3;   // regular mode triples the timer (item 2)

function makeDots(n) {
  // Scatter n dots, biased away from the screen edges and the
  // center where the fiend's face / title sit. Truly random per
  // scare; positions are % of the veil so they scale to any phone.
  const out = [];
  let guard = 0;
  while (out.length < n && guard < 400) {
    guard++;
    const x = 10 + Math.random() * 80; // 10..90%
    const y = 16 + Math.random() * 64; // 16..80%
    // keep them from stacking right on top of each other
    if (out.some((d) => Math.hypot(d.x - x, d.y - y) < 13)) continue;
    out.push({ id: out.length, x, y, hit: false });
  }
  return out;
}

// ============================================================
// <Jumpscare> — the takeover. Fills the screen with the fiend,
// slams the title in, runs the dot-popping puzzle. Self-contained.
// `cursed` controls difficulty (dot count + timer + miss penalty).
// ============================================================
export default function Jumpscare({ scare, cursed = false, onSurvive, onCaught }) {
  const dotCount = cursed ? 6 : 3;                 // item 3
  // Easy mode triples the window; cursed keeps the scare's raw timer.
  const windowMs = cursed ? scare.windowMs : scare.windowMs * EASY_TIME_MULT;

  const [phase, setPhase] = useState("lunge"); // lunge → win | fail
  const [timeLeft, setTimeLeft] = useState(windowMs);
  const [dots, setDots] = useState(() => makeDots(dotCount));
  const doneRef = useRef(false);
  const startRef = useRef(Date.now());

  const Fiend = FIENDS[scare.fiend] || FIENDS.tenant;
  const remaining = dots.filter((d) => !d.hit).length;

  const settle = useCallback(
    (won) => {
      if (doneRef.current) return;
      doneRef.current = true;
      setPhase(won ? "win" : "fail");
      // hold the resolved frame a beat, then hand back to App
      setTimeout(() => {
        if (won) onSurvive && onSurvive();
        else onCaught && onCaught();
      }, won ? 520 : 780);
    },
    [onSurvive, onCaught]
  );

  // pop a dot; clearing the last one = you threw it off
  const hitDot = useCallback(
    (id) => {
      if (doneRef.current) return;
      setDots((ds) => {
        const next = ds.map((d) => (d.id === id ? { ...d, hit: true } : d));
        if (next.every((d) => d.hit)) settle(true);
        return next;
      });
    },
    [settle]
  );

  // a wrong tap — only punishing in Cursed Mode (item 6 parity:
  // easy mode never punishes a misclick, cursed does)
  const missTap = useCallback(() => {
    if (doneRef.current) return;
    if (cursed) settle(false);
  }, [cursed, settle]);

  // countdown
  useEffect(() => {
    const tick = setInterval(() => {
      const left = windowMs - (Date.now() - startRef.current);
      setTimeLeft(left);
      if (left <= 0) settle(false);
    }, 40);
    return () => clearInterval(tick);
  }, [windowMs, settle]);

  const pct = Math.max(0, (timeLeft / windowMs) * 100);
  const danger = pct < 45;

  // animation class drives the lunge / strobe (reduced-motion safe)
  const fxClass =
    phase === "lunge"
      ? REDUCED
        ? "js-cut"
        : "js-lunge"
      : phase === "win"
      ? "js-recede"
      : "js-land";

  return (
    <div style={S.veil} className="js-veil" onPointerDown={missTap}>
      {/* the fiend, filling everything */}
      <div style={S.fiendWrap} className={fxClass}>
        <Fiend phase={phase} />
      </div>

      {/* strobe / vignette overlay */}
      {!REDUCED && phase === "lunge" && <div style={S.strobe} className="js-strobe" />}
      <div style={S.vignette} />

      {/* the slammed-in title */}
      <div style={{ ...S.title, color: danger ? RM.fleshGlow : RM.bone }} className="js-title">
        {phase === "win" ? "—" : scare.title}
      </div>

      {/* the dot puzzle — tap them all before the bar drains */}
      {phase === "lunge" && (
        <>
          {dots.map((d) =>
            d.hit ? null : (
              <button
                key={d.id}
                aria-label="focus"
                className="js-dot"
                style={{
                  ...S.dot,
                  left: `${d.x}%`,
                  top: `${d.y}%`,
                  borderColor: danger ? RM.fleshGlow : RM.ember,
                  boxShadow: emberGlow(danger ? RM.fleshGlow : RM.ember),
                }}
                onPointerDown={(e) => {
                  e.stopPropagation(); // a hit is not a miss
                  hitDot(d.id);
                }}
              />
            )
          )}

          {/* prompt + remaining count + timer bar, pinned low */}
          <div style={S.qte}>
            <div style={S.prompt}>{scare.prompt}</div>
            <div style={S.sub}>
              TAP THE {cursed ? 6 : 3} — {remaining} LEFT
            </div>
            <div style={S.barOuter}>
              <div
                style={{
                  ...S.barInner,
                  width: `${pct}%`,
                  background: danger ? RM.fleshGlow : RM.ember,
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* survive flash line */}
      {phase === "win" && <div style={S.surviveLine}>{scare.survive}</div>}
    </div>
  );
}

const S = {
  veil: {
    position: "fixed",
    inset: 0,
    zIndex: 120, // above manor + inventory, below nothing
    background: RM.char0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  fiendWrap: {
    position: "absolute",
    inset: "-6%",
    transformOrigin: "center 46%",
  },
  strobe: {
    position: "absolute",
    inset: 0,
    background: RM.fleshDeep,
    mixBlendMode: "screen",
    pointerEvents: "none",
  },
  vignette: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(ellipse at 50% 46%, transparent 30%, rgba(7,5,4,0.55) 78%, rgba(7,5,4,0.92) 100%)",
  },
  title: {
    position: "absolute",
    top: "9%",
    width: "100%",
    textAlign: "center",
    fontFamily: RM.fontT,
    fontSize: "clamp(30px, 6.5vw, 64px)",
    letterSpacing: 5,
    textShadow: `0 0 30px ${RM.fleshDeep}, 0 0 60px ${RM.vein}`,
    pointerEvents: "none",
    zIndex: 2,
  },
  dot: {
    position: "absolute",
    zIndex: 4,
    width: 54,
    height: 54,
    marginLeft: -27,
    marginTop: -27,
    borderRadius: "50%",
    background: "radial-gradient(circle at 38% 34%, rgba(255,255,255,0.18), rgba(7,5,4,0.85) 72%)",
    border: "2px solid",
    cursor: "pointer",
    padding: 0,
    touchAction: "manipulation",
  },
  qte: {
    position: "relative",
    zIndex: 3,
    marginBottom: "8vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    padding: "20px 30px",
    background: "rgba(7,5,4,0.72)",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 12,
  },
  prompt: {
    color: RM.bone,
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 18,
    textAlign: "center",
    maxWidth: 420,
  },
  keyCap: {
    minWidth: 92,
    padding: "18px 26px",
    textAlign: "center",
    fontFamily: RM.fontD,
    fontSize: 38,
    fontWeight: 700,
    color: RM.emberBright,
    background: RM.char2,
    border: `2px solid ${RM.ember}`,
    borderRadius: 12,
    cursor: "pointer",
    userSelect: "none",
  },
  sub: {
    color: RM.boneDim,
    fontFamily: RM.fontD,
    letterSpacing: 3,
    fontSize: 13,
  },
  barOuter: {
    width: 300,
    height: 10,
    background: RM.char1,
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 6,
    overflow: "hidden",
  },
  barInner: { height: "100%", transition: "width 50ms linear" },
  surviveLine: {
    position: "relative",
    zIndex: 3,
    marginBottom: "12vh",
    maxWidth: 560,
    textAlign: "center",
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 18,
    color: RM.boneDim,
    lineHeight: 1.55,
    padding: "0 24px",
  },
};
